import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2, Send, FileText, User } from 'lucide-react';
import { cn } from '@/lib/utils';

interface WhatsAppProgressDialogProps {
  open: boolean;
  progress: number;
  status: 'preparing' | 'generating' | 'sending' | 'success';
  guestName?: string;
}

/**
 * 🎨 Этап 5.1: Progress bar для отправки WhatsApp
 * Визуализирует процесс подготовки и отправки счета через WhatsApp
 */
export const WhatsAppProgressDialog: React.FC<WhatsAppProgressDialogProps> = ({
  open,
  progress,
  status,
  guestName
}) => {
  const getStatusInfo = () => {
    switch (status) {
      case 'preparing':
        return {
          icon: <FileText className="h-8 w-8 text-blue-500 animate-pulse" />,
          title: 'Подготовка счета',
          description: 'Формируем данные для счета...',
          color: 'bg-blue-500'
        };
      case 'generating':
        return {
          icon: <User className="h-8 w-8 text-purple-500 animate-pulse" />,
          title: 'Генерация сообщения',
          description: `Создаем персонализированное сообщение для ${guestName || 'гостя'}...`,
          color: 'bg-purple-500'
        };
      case 'sending':
        return {
          icon: <Send className="h-8 w-8 text-green-500 animate-bounce" />,
          title: 'Отправка через WhatsApp',
          description: 'Открываем WhatsApp Web...',
          color: 'bg-green-500'
        };
      case 'success':
        return {
          icon: <CheckCircle2 className="h-8 w-8 text-emerald-500" />,
          title: 'Готово!',
          description: 'Счет успешно отправлен гостю',
          color: 'bg-emerald-500'
        };
      default:
        return {
          icon: <FileText className="h-8 w-8 text-muted-foreground" />,
          title: 'Обработка',
          description: 'Пожалуйста, подождите...',
          color: 'bg-muted'
        };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <Dialog open={open}>
      <DialogContent className="sm:max-w-md [&>button]:hidden">
        <DialogHeader>
          <div className="flex flex-col items-center space-y-4">
            <div className="relative">
              <div className={cn(
                "absolute inset-0 rounded-full blur-xl opacity-50",
                statusInfo.color
              )} />
              <div className="relative bg-background rounded-full p-4">
                {statusInfo.icon}
              </div>
            </div>
            <DialogTitle className="text-center text-xl">
              {statusInfo.title}
            </DialogTitle>
            <DialogDescription className="text-center">
              {statusInfo.description}
            </DialogDescription>
          </div>
        </DialogHeader>
        
        <div className="space-y-4">
          <Progress 
            value={progress} 
            className="h-2 transition-all duration-300"
          />
          <p className="text-sm text-center text-muted-foreground">
            {progress}%
          </p>
        </div>

        {/* Этапы процесса */}
        <div className="space-y-2 mt-4">
          <div className={cn(
            "flex items-center gap-2 text-sm transition-all",
            progress >= 25 ? "text-foreground" : "text-muted-foreground"
          )}>
            <CheckCircle2 className={cn(
              "h-4 w-4",
              progress >= 25 && "text-blue-500"
            )} />
            <span>Подготовка данных</span>
          </div>
          <div className={cn(
            "flex items-center gap-2 text-sm transition-all",
            progress >= 50 ? "text-foreground" : "text-muted-foreground"
          )}>
            <CheckCircle2 className={cn(
              "h-4 w-4",
              progress >= 50 && "text-purple-500"
            )} />
            <span>Генерация сообщения</span>
          </div>
          <div className={cn(
            "flex items-center gap-2 text-sm transition-all",
            progress >= 75 ? "text-foreground" : "text-muted-foreground"
          )}>
            <CheckCircle2 className={cn(
              "h-4 w-4",
              progress >= 75 && "text-green-500"
            )} />
            <span>Открытие WhatsApp</span>
          </div>
          <div className={cn(
            "flex items-center gap-2 text-sm transition-all",
            progress >= 100 ? "text-foreground" : "text-muted-foreground"
          )}>
            <CheckCircle2 className={cn(
              "h-4 w-4",
              progress >= 100 && "text-emerald-500"
            )} />
            <span>Отправка завершена</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
